import { z } from 'zod';
import { AccountStatusSchema, IdentitySchema, PersonKindSchema } from './people';

/**
 * Phase 1 M6: identities, person editing, new starters and leavers, the activity timeline,
 * the dt_spar write bridge and the data-driven Home tasks (spec §2 rules 2–3, §5 screens 2 and
 * 6, §7; D6, D26, D27).
 */

const IsoDate = z.iso.date();

// Identities ----------------------------------------------------------------------

/** The systems a person can be linked to from People; legacy_* ids come only from the import. */
export const LinkableIdentitySystemSchema = z.enum([
  'autopart_inits',
  'quartix_driver',
  'pbx_extension',
  'rfid_fob',
]);
export type LinkableIdentitySystem = z.infer<typeof LinkableIdentitySystemSchema>;

export const IDENTITY_SYSTEM_LABEL: Record<z.infer<typeof IdentitySchema>['system'], string> = {
  autopart_inits: 'Autopart initials',
  quartix_driver: 'Quartix driver',
  pbx_extension: 'Phone extension',
  rfid_fob: 'Clocking fob',
  legacy_user_id: 'Old login id',
  legacy_staff_id: 'Old staff id',
  legacy_username: 'Old username',
};

export const IdentityCreateSchema = z
  .object({
    system: LinkableIdentitySystemSchema,
    value: z.string().trim().min(1).max(64),
    /** Site code; initials repeat across branches, so they are normally scoped to one. */
    siteCode: z.string().trim().max(32).nullable().optional(),
    from: IsoDate.nullable().optional(),
    to: IsoDate.nullable().optional(),
  })
  .refine((i) => !i.from || !i.to || i.to >= i.from, {
    message: 'End date is before the start',
    path: ['to'],
  });
export type IdentityCreate = z.infer<typeof IdentityCreateSchema>;

export const IdentitiesResponseSchema = z.object({ identities: z.array(IdentitySchema) });
export type IdentitiesResponse = z.infer<typeof IdentitiesResponseSchema>;

/** 409 when the same value is already live for someone else at that site. */
export const IdentityTakenErrorSchema = z.object({
  error: z.literal('identity_taken'),
  message: z.string(),
  holder: z.object({ personId: z.uuid(), name: z.string(), identityId: z.uuid() }),
});
export type IdentityTakenError = z.infer<typeof IdentityTakenErrorSchema>;

// Person editing ------------------------------------------------------------------

const Name = z.string().trim().min(1).max(100);

export const PersonPatchSchema = z.object({
  givenName: Name.optional(),
  familyName: Name.optional(),
  preferredName: z.string().trim().max(100).nullable().optional(),
  /** External people only: who they work for. */
  kind: PersonKindSchema.optional(),
  organisation: z.string().trim().max(100).nullable().optional(),
  /** Changing it clears the verified flag and sends a verification link. */
  loginEmail: z.email().trim().toLowerCase().max(255).nullable().optional(),
  staff: z
    .object({
      jobTitle: z.string().trim().max(100).nullable().optional(),
      homeSiteCode: z.string().trim().max(32).nullable().optional(),
      lineManagerPositionCode: z.string().trim().max(64).nullable().optional(),
      startDate: IsoDate.nullable().optional(),
      contractHours: z.number().min(0).max(168).nullable().optional(),
    })
    .optional(),
});
export type PersonPatch = z.infer<typeof PersonPatchSchema>;

export const PersonPatchResponseSchema = z.object({
  ok: z.literal(true),
  /** A verification email went to the new address. */
  verificationSent: z.boolean(),
  changed: z.array(z.string()),
});
export type PersonPatchResponse = z.infer<typeof PersonPatchResponseSchema>;

export const VerifyEmailResponseSchema = z.object({
  ok: z.literal(true),
  email: z.string(),
});

// New starter ---------------------------------------------------------------------

export const PersonCreateSchema = z
  .object({
    givenName: Name,
    familyName: Name,
    preferredName: z.string().trim().max(100).nullable().optional(),
    kind: PersonKindSchema.default('staff'),
    organisation: z.string().trim().max(100).nullable().optional(),
    staffNumber: z.string().trim().max(32).nullable().optional(),
    homeSiteCode: z.string().trim().max(32).nullable().optional(),
    jobTitle: z.string().trim().max(100).nullable().optional(),
    startDate: IsoDate.nullable().optional(),
    lineManagerPositionCode: z.string().trim().max(64).nullable().optional(),
    contractHours: z.number().min(0).max(168).nullable().optional(),
    loginEmail: z.email().trim().toLowerCase().max(255).nullable().optional(),
    /** Create a login and email an invite (needs a login email or a staff number). */
    createAccount: z.boolean().default(false),
    /** Seed access: a role at the home site, or a seat. */
    initialRoleCode: z.string().trim().max(64).nullable().optional(),
    positionCode: z.string().trim().max(64).nullable().optional(),
  })
  .refine((p) => p.kind === 'external' || p.homeSiteCode, {
    message: 'Staff need a home site',
    path: ['homeSiteCode'],
  })
  .refine((p) => !p.createAccount || p.loginEmail || p.staffNumber, {
    message: 'A login needs an email address or a staff number to sign in with',
    path: ['loginEmail'],
  });
export type PersonCreate = z.infer<typeof PersonCreateSchema>;

export const PersonCreatedSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  accountCreated: z.boolean(),
  /** The invite went to the login email; false when the account has no email (staff number only). */
  inviteSent: z.boolean(),
  positionAssigned: z.boolean(),
  roleGranted: z.boolean(),
});
export type PersonCreated = z.infer<typeof PersonCreatedSchema>;

// Leavers ---------------------------------------------------------------------------

export const LeaveRequestSchema = z.object({
  endDate: IsoDate,
  reason: z.string().trim().max(500).nullable().optional(),
});
export type LeaveRequest = z.infer<typeof LeaveRequestSchema>;

/** What a leaver run did (or will do on the day). */
export const LeaveSummarySchema = z.object({
  personId: z.uuid(),
  endDate: IsoDate,
  /** True when the date is in the future: recorded now, applied by the nightly job. */
  scheduled: z.boolean(),
  accountDisabled: z.boolean(),
  sessionsRevoked: z.number().int().nonnegative(),
  seatsVacated: z.array(z.object({ positionCode: z.string(), title: z.string() })),
  grantsRevoked: z.number().int().nonnegative(),
  coversRemoved: z.number().int().nonnegative(),
  identitiesEnded: z.number().int().nonnegative(),
});
export type LeaveSummary = z.infer<typeof LeaveSummarySchema>;

export const REJOIN_WINDOW_DAYS = 30;

export const RejoinResponseSchema = z.object({
  ok: z.literal(true),
  accountEnabled: z.boolean(),
});

// Activity timeline -----------------------------------------------------------------

export const ActivityFilterSchema = z.enum(['all', 'changes', 'signins']);
export type ActivityFilter = z.infer<typeof ActivityFilterSchema>;

export const ActivityQuerySchema = z.object({
  filter: ActivityFilterSchema.default('all'),
  /** Keyset cursor: items strictly before this instant. */
  before: z.iso.datetime().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type ActivityQuery = z.infer<typeof ActivityQuerySchema>;

export const ActivityIconSchema = z.enum([
  'login',
  'key',
  'promotion',
  'edit',
  'identity',
  'access',
  'leaver',
  'bridge',
  'warn',
]);

export const ActivityItemSchema = z.object({
  id: z.string(),
  kind: z.enum(['change', 'signin']),
  at: z.iso.datetime(),
  /** The audit action code, or `login.day` for a day's sign-ins. */
  action: z.string(),
  icon: ActivityIconSchema,
  /** Who did it; null for the system (nightly job, script). */
  actor: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** Sentence without the actor: "ended Branch Manager, Stafford." */
  text: z.string(),
  /** Extra line, e.g. the failure count for a day of sign-ins. */
  detail: z.string().nullable(),
});
export type ActivityItem = z.infer<typeof ActivityItemSchema>;

export const ActivityResponseSchema = z.object({
  items: z.array(ActivityItemSchema),
  nextBefore: z.iso.datetime().nullable(),
});
export type ActivityResponse = z.infer<typeof ActivityResponseSchema>;

// Write bridge (D6, D27) -------------------------------------------------------------

export const BridgeModeSchema = z.enum(['off', 'dry_run', 'on']);
export type BridgeMode = z.infer<typeof BridgeModeSchema>;

export const BridgeLogEntrySchema = z.object({
  id: z.uuid(),
  personId: z.uuid().nullable(),
  personName: z.string().nullable(),
  /** M3 adds the holiday mirror (bridge/holidays.ts); 2b-M3 the absence mirror (bridge/absence.ts). */
  operation: z.enum([
    'password',
    'active',
    'holiday_booking',
    'holiday_allocation',
    'absence_mirror',
    'fleet_mirror',
    'safety_mirror',
  ]),
  targetCount: z.number().int().nonnegative(),
  rowsAffected: z.number().int().nonnegative().nullable(),
  mode: BridgeModeSchema,
  ok: z.boolean(),
  error: z.string().nullable(),
  at: z.iso.datetime(),
});
export type BridgeLogEntry = z.infer<typeof BridgeLogEntrySchema>;

export const BridgeStatusSchema = z.object({
  mode: BridgeModeSchema,
  /** M3: HOLIDAY_BRIDGE, switched independently of the password bridge. */
  holidayMode: BridgeModeSchema,
  /** 2b-M3: ABSENCE_BRIDGE, switched independently too. */
  absenceMode: BridgeModeSchema,
  /** Fleet M2: FLEET_BRIDGE, switched independently too. */
  fleetMode: BridgeModeSchema,
  safetyMode: BridgeModeSchema.optional(),
  lastSuccess: BridgeLogEntrySchema.nullable(),
  lastFailure: BridgeLogEntrySchema.nullable(),
  /** Failed writes not yet retried successfully. */
  pendingRetries: z.number().int().nonnegative(),
});
export type BridgeStatus = z.infer<typeof BridgeStatusSchema>;

export const BridgeRetryResponseSchema = z.object({
  ok: z.boolean(),
  entry: BridgeLogEntrySchema,
});

// Home tasks (D26) --------------------------------------------------------------------

export const HomeTaskSchema = z.object({
  id: z.string(),
  title: z.string(),
  count: z.number().int().positive(),
  /** Where "Review" goes. */
  to: z.string(),
  tone: z.enum(['warn', 'danger', 'muted', 'info']),
});
export type HomeTask = z.infer<typeof HomeTaskSchema>;

export const HomeTasksResponseSchema = z.object({ tasks: z.array(HomeTaskSchema) });
export type HomeTasksResponse = z.infer<typeof HomeTasksResponseSchema>;

// Locked accounts queue --------------------------------------------------------------

export const LockedAccountSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  status: AccountStatusSchema,
  lockedUntil: z.iso.datetime().nullable(),
  siteName: z.string().nullable(),
});
